import type { EvaluationReview } from '@shared/domain/review';
import type { LegalCheckFlag } from '../../legal-check/types';
import { Button } from '@mui/material';
import { ReviewStep } from '@shared/domain/review';
import React from 'react';
import { EVALUATION_FORM_FIELDS } from '../../data';
import { getReviewStepName } from '../../helpers/ReviewStepNames';
import { useReviewForm } from '../../hooks/useReviewForm';
import { QuestionRenderer } from '../QuestionRenderer';
import '../../styles/review-form.scss';

interface EvaluationReviewProps {
  submitReviewForm?: (formFields: EvaluationReview, reviewStep: ReviewStep, isDraft?: boolean) => void;
  editMode?: boolean;
  defaultValues?: Partial<EvaluationReview> & { id?: number };
  handleEditSuccess?: () => void;
  seedLegalCheckFlags?: Record<string, LegalCheckFlag[]>;
}

function EvaluationReviewForm(props: EvaluationReviewProps) {
  const { submitReviewForm, editMode, defaultValues, handleEditSuccess, seedLegalCheckFlags } = props;

  const {
    formFields,
    extendFormFields,
    setFormFields,
    errors,
    validateField,
    handleFormFieldChange,
    handleSubmit,
    handleEdit,
    getLegalCheckBindings,
  } = useReviewForm<EvaluationReview>({
    reviewStep: ReviewStep.EVALUATION,
    formFields: EVALUATION_FORM_FIELDS,
    editMode,
    defaultValues,
    onSubmit: submitReviewForm,
    onEditSuccess: handleEditSuccess,
    seedLegalCheckFlags,
  });

  return (
    <div className="review-page">
      <div data-testid="evaluation-review-step-content-anchor" />

      <header className="review-page__header">
        <p className="review-page__eyebrow">Step 4 · Evaluation</p>
        <h1 className="review-page__title">{editMode ? 'Edit the evaluation' : 'How did the buyer evaluate you?'}</h1>
        <p className="review-page__subtitle">
          Trials, security review, decision-maker count, criteria changes — the shape of the evaluation tells the next
          AE what they're walking into.
        </p>
      </header>

      <form>
        <section className="review-section">
          <QuestionRenderer
            questions={EVALUATION_FORM_FIELDS}
            formFields={formFields}
            errors={errors}
            handleFormFieldChange={handleFormFieldChange}
            extendFormFields={extendFormFields}
            validateField={validateField}
            setFormFields={setFormFields}
            getLegalCheckBindings={getLegalCheckBindings}
          />
        </section>
      </form>

      <div className="review-save-bar">
        <Button
          variant="contained"
          className="review-save-bar__button"
          data-testid="review-save-button"
          onClick={editMode ? handleEdit : handleSubmit}
        >
          {editMode ? `Save ${getReviewStepName(ReviewStep.EVALUATION)} changes` : 'Continue to Legal'}
        </Button>
      </div>
    </div>
  );
}

export default EvaluationReviewForm;
